import React from 'react';
import { ActionItemStatus, getProviderStripeConnectProfileLink } from '@uplift/api';
import { AsyncButton, BaseDashboardActionItem, ConditionalWrapper, IconWithText, LightChip, ProgressIcon, Tooltip } from '@uplift/ui-v2';
import { useSharedStyles } from '../../styles';
import { AccountBalanceOutlined, LaunchRounded } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { getChipColor } from '../../helpers';
import { useL10n } from '@uplift/hooks';
import { useCurrentuser } from '@uplift/contexts';
import { useGetProviderStripeConnectDetailsQuery } from '@uplift/queries';
import { asyncWait } from '@uplift/utils';


const StripeConnectProfileLink = () => {
  const me = useCurrentuser();
  const { classes: sharedClasses } = useSharedStyles();
  const [ isLoading, setIsLoading ] = React.useState(false);

  return (
    <IconWithText
      placement="end"
      icon={isLoading ? <ProgressIcon sx={{ fontSize: 16 }} /> : <LaunchRounded sx={{ fontSize: 16 }} color="action" />}
    >
      <span
        className={sharedClasses.link}
        onClick={async () => {
          if (isLoading) return;
          setIsLoading(true);
          try {
            const profileLink = await getProviderStripeConnectProfileLink(me.id);

            window.open(profileLink.data, '_self');
          } catch (err) {
            console.error(err);
            setIsLoading(false);
          }
        }}
      >Edit Account</span>
    </IconWithText>
  );
};


export type ConfigurePayoutAccountProps = {
  status: ActionItemStatus;
};


export const ConfigurePayoutAccount: React.VFC<ConfigurePayoutAccountProps> = ({ status }) => {
  const me = useCurrentuser();
  const l10n = useL10n();
  const { classes: sharedClasses } = useSharedStyles();
  const { data } = useGetProviderStripeConnectDetailsQuery(me.id);

  if (status === ActionItemStatus.todo) {
    return (
      <BaseDashboardActionItem
        title={<span>Set up your <strong>Payouts Account</strong> through Stripe</span>}
        body={<span>Est time to complete: <strong>5</strong> min</span>}
        bodyClassName={sharedClasses.actionItemSubtitle}
        color="primary"
        icon={<AccountBalanceOutlined />}
        actionButton={(
          <ConditionalWrapper
            condition={!data}
            wrapper={children => (
              <Tooltip title="Payouts account setup is not available right now" placement="bottom">
                <span>
                  {children}
                </span>
              </Tooltip>
            )}
          >
            <AsyncButton
              variant="contained"
              color="secondary"
              onClick={async () => {
                const profileLink = await getProviderStripeConnectProfileLink(me.id);

                window.open(profileLink.data, '_self');
                await asyncWait(10000);
              }}
              disabled={!data}
            >
              Set up
            </AsyncButton>
          </ConditionalWrapper>
        )}
      />
    );
  }

  return (
    <Box className={sharedClasses.progressSummaryStepWithChip}>
      <Box display="flex" flexDirection="column">
        <Typography variant="h5">Payouts Account</Typography>
        <StripeConnectProfileLink />
      </Box>
      <LightChip label={l10n.dashboard.actionItems.status[status]} color={getChipColor(status)} size="small" />
    </Box>
  );
};
